/**
 * The editor for a `selectWithCustom` row: a closed list of choices with a
 * free-text escape hatch (VALE-1227, `components/attributes/CLAUDE.md` §3).
 *
 * **Two controls, one row, and which one shows is state rather than a prop.**
 * The row opens on the picker, because that is the answer for almost everybody;
 * choosing *Custom* replaces it with a text input **without committing**, so
 * the value the row saves is always something the reader actually chose or
 * typed. An existing custom value opens straight into the input, since the
 * picker has nothing to show for it and offering `Custom` as the selected item
 * would hide the value the row is meant to be reading out.
 *
 * The commit contract is each control's own, unchanged:
 *
 * · The picker commits on choose and reverts on dismiss, exactly as the shared
 *   `select` branch does — so `Escape` and an outside click behave identically.
 * · The input commits on blur and on `Enter`, reverts on `Escape`, and takes
 *   `Tab` over so it advances to the next field rather than leaving the card.
 *
 * **The one thing this control must never do is commit the `Custom` sentinel.**
 * It is a marker for "the reader wants to type", not a value — the draft the
 * grid moves around always holds a real value or the empty string, which is
 * what keeps a spec's `toPatch` from having to know this control exists.
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_TEXT
} from './attribute-tokens';
import type { AttributeDraft, AttributeOption } from './attribute-types';

/**
 * The picker item that means *let me type one*.
 *
 * Prefixed and suffixed so it cannot collide with a real value from any
 * vocabulary this kind is used for — the same reason the `Office` row's
 * `NO_OFFICE` is shaped this way rather than being an empty string.
 */
const CUSTOM = '__custom__';

/** Whether a draft is a value the picker can show as selected. */
function isListed(value: string, options: AttributeOption[]): boolean {
  return options.some((option) => option.value === value);
}

/**
 * **The row's read-out — the chosen option's label, or the custom text as
 * typed.** `null` when the row holds nothing, so the caller supplies its own
 * empty state.
 *
 * Exported for the row's commit bridge (VALE-1773) — see
 * `attribute-draft-display.ts`. An unlisted value *is* the custom one, so it
 * renders verbatim rather than falling back to a placeholder: that is the whole
 * point of this kind.
 */
export function formatSelectWithCustomSummary(
  value: string,
  options: AttributeOption[]
): string | null {
  if (!value) return null;
  return options.find((option) => option.value === value)?.label ?? value;
}

export function AttributeSelectWithCustomEditor({
  label,
  draft,
  options,
  customLabel = 'Custom',
  customPlaceholder,
  unified,
  onDraft,
  onRevert,
  onCommit,
  onStep
}: {
  label: string;
  draft: AttributeDraft;
  options: AttributeOption[];
  customLabel?: string;
  customPlaceholder?: string;
  unified: boolean;
  onDraft: (next: AttributeDraft) => void;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
  onStep: (value: AttributeDraft, delta: number) => void;
}) {
  const text = typeof draft === 'string' ? draft : '';

  /*
    Held as state rather than derived on every render, because *choosing*
    `Custom` has to move the row into the input while the draft is still the
    listed value it opened on. Seeded from the draft so a stored custom value
    opens in the input it was typed into.
  */
  const [typing, setTyping] = useState(
    () => text.length > 0 && !isListed(text, options)
  );

  if (typing) {
    return (
      <Input
        autoFocus
        aria-label={label}
        placeholder={customPlaceholder}
        value={isListed(text, options) ? '' : text}
        // Select-all on open, matching the shared text branch: an open editor
        // is a correction more often than an addition.
        onFocus={(event) => event.currentTarget.select()}
        onChange={(event) => onDraft(event.target.value)}
        onBlur={(event) => onCommit(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            // Same halved responsibility as the shared text branch: the host
            // owns the other half via `onEditingChange` → `onEscapeKeyDown`,
            // because Radix listens above React's root.
            event.preventDefault();
            onRevert();
          } else if (event.key === 'Enter') {
            event.preventDefault();
            onCommit(event.currentTarget.value);
          } else if (event.key === 'Tab') {
            event.preventDefault();
            onStep(event.currentTarget.value, event.shiftKey ? -1 : 1);
          }
        }}
        className={cn(
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_INSET,
          ATTRIBUTE_LEADING,
          unified && ATTRIBUTE_EDITOR_BARE
        )}
      />
    );
  }

  return (
    <Select
      // Opens on mount, so clicking the value shows the options immediately
      // rather than parking a closed control under the pointer.
      defaultOpen
      value={isListed(text, options) ? text : ''}
      onValueChange={(next) => {
        if (next === CUSTOM) {
          /*
            Move to the input and clear the draft — but do NOT commit. The
            sentinel is not a value, and committing here would save whichever
            listed option the row happened to open on as though the reader had
            chosen it.
          */
          onDraft('');
          setTyping(true);
          return;
        }
        onCommit(next);
      }}
      onOpenChange={(next) => {
        /*
          Dismissed without choosing: revert rather than commit, so `Escape` and
          an outside click behave identically. Guarded on `typing` because
          choosing `Custom` closes the picker too — reverting there would undo
          the switch the reader just asked for.
        */
        if (!next && !typing) onRevert();
      }}
    >
      <SelectTrigger
        hideArrow
        aria-label={label}
        className={cn(
          'h-auto w-full justify-start bg-transparent',
          ATTRIBUTE_VALUE_TEXT,
          ATTRIBUTE_INSET,
          ATTRIBUTE_LEADING,
          ATTRIBUTE_VALUE_INK,
          unified && ATTRIBUTE_EDITOR_BARE
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="start">
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className={ATTRIBUTE_VALUE_TEXT}
          >
            {option.label}
          </SelectItem>
        ))}
        {/*
          Last, and after the vocabulary rather than before it: the listed
          choices are the answer for almost everybody, and a `Custom` row above
          them would read as the primary path.
        */}
        <SelectItem value={CUSTOM} className={ATTRIBUTE_VALUE_TEXT}>
          {customLabel}
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
